import React, { useState, useEffect } from 'react';
import { getOriginUrl, COS_CDN_BASE } from '../lib/constants';

interface CosImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  // 可以传递原始 URL，如果不传则使用 src
  fallbackSrc?: string;
}

/**
 * 带有 CDN 容灾功能的图片组件
 * 默认尝试加载 src (CDN URL)，如果失败则尝试加载 fallbackSrc 或自动计算出的源站 URL
 */
const CosImage: React.FC<CosImageProps> = ({ src, fallbackSrc, alt, onError, ...props }) => {
  const [currentSrc, setCurrentSrc] = useState<string | undefined>(src);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setCurrentSrc(src);
    setHasError(false);
  }, [src]);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // 如果已经出错过了，或者没有 src，则不再处理
    if (hasError || !currentSrc) {
      if (onError) onError(e);
      return;
    }

    // 标记为已出错，防止无限循环
    setHasError(true);

    // 尝试切换到源站 URL
    const originUrl = getOriginUrl(currentSrc);
    
    // 如果计算出的源站 URL 与当前不同，则尝试加载
    if (originUrl && originUrl !== currentSrc) {
      console.warn(`Image load failed: ${currentSrc}, retrying with origin: ${originUrl}`);
      setCurrentSrc(originUrl);
    } else if (fallbackSrc && fallbackSrc !== currentSrc) {
      // 如果有显式的 fallbackSrc
      setCurrentSrc(fallbackSrc);
    } else {
      // 无法处理，向上抛出错误
      if (onError) onError(e);
    }
  };

  return (
    <img 
      src={currentSrc} 
      alt={alt} 
      onError={handleError}
      {...props} 
    />
  );
};

export default CosImage;
